import { useState } from 'react'
import { ActionIcon, Badge, Button, Group, Modal, Stack, TextInput, Title, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { DataTable } from 'mantine-datatable'
import { IconEdit, IconPlus, IconSearch } from '@tabler/icons-react'
import type { Proveedor, ProveedorEntrada } from '@serkat/shared'
import { useSesion } from '../auth/useSesion'
import { ruc as fmtRuc, vacio } from '../../compartido/formato'
import { FormularioProveedor } from './componentes/FormularioProveedor'
import { useGuardarProveedor, useProveedores } from './hooks/useProveedores'

const POR_PAGINA = 15

export function ProveedoresPagina() {
  const { usuario } = useSesion()
  const [pagina, setPagina] = useState(1)
  const [texto, setTexto] = useState('')
  const [buscar] = useDebouncedValue(texto, 300)
  const [editando, setEditando] = useState<Proveedor | undefined>()
  const [abierto, modal] = useDisclosure(false)

  const { data, isFetching } = useProveedores({
    pagina,
    porPagina: POR_PAGINA,
    buscar: buscar || undefined,
  })
  const { crear, actualizar } = useGuardarProveedor()

  // El backend decide de verdad; esto solo evita mostrar un botón condenado al 403.
  const puedeEditar = usuario?.rol === 'PROCURA' || usuario?.rol === 'ADMIN'

  function abrir(p?: Proveedor) {
    setEditando(p)
    modal.open()
  }

  function guardar(datos: ProveedorEntrada) {
    const alTerminar = { onSuccess: () => modal.close() }
    if (editando) actualizar.mutate({ id: editando.id, datos }, alTerminar)
    else crear.mutate(datos, alTerminar)
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Proveedores</Title>
        {puedeEditar && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => abrir()}>
            Nuevo proveedor
          </Button>
        )}
      </Group>

      <TextInput
        placeholder="Buscar por razón social, RUC o rubro"
        leftSection={<IconSearch size={16} />}
        value={texto}
        onChange={(e) => {
          setTexto(e.currentTarget.value)
          setPagina(1)
        }}
        maw={420}
      />

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        minHeight={220}
        fetching={isFetching}
        records={data?.datos ?? []}
        totalRecords={data?.total ?? 0}
        recordsPerPage={POR_PAGINA}
        page={pagina}
        onPageChange={setPagina}
        noRecordsText="No hay proveedores registrados"
        idAccessor="id"
        columns={[
          { accessor: 'razonSocial', title: 'Razón social', width: '32%' },
          {
            accessor: 'ruc',
            title: 'RUC',
            render: (p) => <span style={{ fontFamily: 'monospace' }}>{fmtRuc(p.ruc)}</span>,
          },
          { accessor: 'rubro', title: 'Rubro', render: (p) => vacio(p.rubro) },
          { accessor: 'contactoNombre', title: 'Contacto', render: (p) => vacio(p.contactoNombre) },
          {
            accessor: 'activo',
            title: 'Estado',
            width: 110,
            render: (p) => (
              <Badge color={p.activo ? 'teal' : 'gray'} variant="light" size="sm">
                {p.activo ? 'Activo' : 'Inactivo'}
              </Badge>
            ),
          },
          {
            accessor: 'acciones',
            title: '',
            width: 60,
            render: (p) =>
              puedeEditar && (
                <Tooltip label="Editar">
                  <ActionIcon variant="subtle" onClick={() => abrir(p)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              ),
          },
        ]}
      />

      <Modal
        opened={abierto}
        onClose={modal.close}
        title={editando ? 'Editar proveedor' : 'Nuevo proveedor'}
        size="lg"
      >
        <FormularioProveedor
          inicial={editando}
          guardando={crear.isPending || actualizar.isPending}
          onGuardar={guardar}
          onCancelar={modal.close}
        />
      </Modal>
    </Stack>
  )
}
